/**
 * Fallback Utility function to copy text to the user's clipboard for old browsers that doesn't support Clipboard API
 * @param {string} text - The text to be copied
 * @see https://hackernoon.com/copying-text-to-clipboard-with-javascript-df4d4988697f
 * @see https://www.w3schools.com/howto/howto_js_copy_clipboard.asp
 */
export const copyToClipboard = (text: string, showNotification?: () => void) => {
  // Create Fake Element to house text
  const fakeElem = document.createElement('textarea');

  // Prevent zooming on iOS
  fakeElem.style.fontSize = '12pt';

  // Reset box model
  fakeElem.style.border = '0';
  fakeElem.style.padding = '0';
  fakeElem.style.margin = '0';

  // Move element out of screen horizontally
  fakeElem.style.position = 'absolute';
  const isRTL = document.documentElement.getAttribute('dir') === 'rtl';
  fakeElem.style[isRTL ? 'right' : 'left'] = '-9999px';

  // Move element to the same position vertically
  const yPosition = window.pageYOffset || document.documentElement.scrollTop;
  fakeElem.style.top = `${yPosition}px`;

  // Allow for easier text selection
  fakeElem.setAttribute('readonly', '');

  fakeElem.value = text;

  document.body.appendChild(fakeElem);
  fakeElem.select();
  fakeElem.setSelectionRange(0, fakeElem.value.length);

  document.execCommand('copy');

  document.getSelection()?.removeAllRanges();
  fakeElem.remove();

  if (showNotification && typeof showNotification === 'function') {
    showNotification();
  }
};

/**
 * Utility function to copy text to the user's clipboard with Clipboard API
 * @param {string} text - The text to be copied
 * @returns {Promise} - Returns a promise. When the promise ends the text was copied to the user's clipboard.
 * Use this promise to show progress messages or status messages to the user while is processing.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Clipboard_API
 */
export function asyncCopyToClipboard(text: string): Promise<void> {
  // Check browser's Clipboard API support
  if (!navigator.clipboard) {
    // Copy text using old legacy method
    return new Promise(resolve => {
      copyToClipboard(text);
      resolve();
    });
  }

  // Copy text using Clipboard API
  return navigator.clipboard.writeText(text);
}
